/**
 * Shared fleet colour vocabulary.
 *
 * One place so the Gantt bar, the calendar dot, the bookings table row and the
 * booking form all say the same thing with the same colour. Every tone carries
 * a dark variant because the app themes off a `.dark` root class.
 */

import { isOverdue } from '../../lib/fleet';

/**
 * How a booking bar reads on the timeline.
 *
 * Three tones carry the schedule — reserved, out, service. Overdue is not a
 * fourth: it is an `out` booking wearing a warning, which is why it needs no
 * legend entry to be understood.
 */
export function bookingTone(booking, now = new Date()) {
  if (booking.status === 'cancelled' || booking.status === 'no_show') return 'cancelled';
  if (booking.type === 'maintenance') return 'service';
  if (booking.status === 'returned') return 'returned';
  if (booking.status === 'out') return isOverdue(booking, now) ? 'overdue' : 'out';
  return 'reserved';
}

/** The three tones that appear in the legend, in reading order. */
export const LEGEND_TONES = ['reserved', 'out', 'service'];

export const BOOKING_TONE_STYLES = {
  reserved: {
    bar: 'bg-sky-100 border-sky-400 text-sky-950 dark:bg-sky-950 dark:border-sky-600 dark:text-sky-100',
    dot: 'bg-sky-500',
    label: 'Reserved'
  },
  out: {
    bar: 'bg-emerald-100 border-emerald-500 text-emerald-950 dark:bg-emerald-950 dark:border-emerald-600 dark:text-emerald-100',
    dot: 'bg-emerald-500',
    label: 'Out'
  },
  service: {
    bar: 'bg-amber-100 border-amber-500 text-amber-950 dark:bg-amber-950 dark:border-amber-600 dark:text-amber-100',
    dot: 'bg-amber-500',
    label: 'Service'
  },
  // Not in the legend — see bookingTone.
  overdue: {
    bar: 'bg-red-100 border-red-500 text-red-950 dark:bg-red-950 dark:border-red-600 dark:text-red-100',
    dot: 'bg-red-500',
    label: 'Overdue'
  },
  returned: {
    bar: 'bg-muted border-border text-muted-foreground',
    dot: 'bg-muted-foreground',
    label: 'Returned'
  },
  cancelled: {
    bar: 'bg-transparent border-dashed border-muted-foreground/50 text-muted-foreground line-through',
    dot: 'bg-muted-foreground/50',
    label: 'Cancelled'
  }
};

/** Compliance severity — used by badges, the alerts panel and the vehicle list. */
export const COMPLIANCE_TONE_STYLES = {
  expired: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950 dark:text-red-200 dark:border-red-800',
  due_soon: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800',
  ok: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800',
  unknown: 'bg-muted text-muted-foreground border-border',
  not_set: 'bg-muted text-muted-foreground border-border',
  disabled: 'bg-muted text-muted-foreground border-border'
};

export const COMPLIANCE_LABELS = {
  expired: 'Expired',
  due_soon: 'Due soon',
  ok: 'Current',
  unknown: 'Unknown',
  not_set: 'Not set',
  disabled: 'Reminders off'
};

/** Live state of a vehicle right now. */
export const VEHICLE_STATE_STYLES = {
  available: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800',
  reserved: 'bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-950 dark:text-sky-200 dark:border-sky-800',
  on_loan: 'bg-emerald-500 text-white border-emerald-600',
  allocated: 'bg-emerald-500 text-white border-emerald-600',
  maintenance: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800',
  off_road: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950 dark:text-red-200 dark:border-red-800',
  retired: 'bg-muted text-muted-foreground border-border'
};
